import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

interface WorkspaceCardRegistration {
  dirty: boolean
  discard: () => void
}

interface WorkspaceEditGuard {
  hasUnsavedChanges: boolean
  registerCard: (id: string, entry: WorkspaceCardRegistration) => () => void
  discardAll: () => void
}

const WorkspaceEditGuardContext = createContext<WorkspaceEditGuard | null>(null)

function WorkspaceEditGuardProvider({ children }: { children: ReactNode }) {
  const cards = useRef(new Map<string, WorkspaceCardRegistration>())
  const [dirtyCount, setDirtyCount] = useState(0)

  const sync = useCallback(() => {
    let next = 0
    cards.current.forEach((entry) => {
      if (entry.dirty) next += 1
    })
    setDirtyCount(next)
  }, [])

  const registerCard = useCallback(
    (id: string, entry: WorkspaceCardRegistration) => {
      cards.current.set(id, entry)
      sync()

      return () => {
        cards.current.delete(id)
        sync()
      }
    },
    [sync]
  )

  const discardAll = useCallback(() => {
    cards.current.forEach((entry) => entry.discard())
  }, [])

  const value = useMemo(
    () => ({ hasUnsavedChanges: dirtyCount > 0, registerCard, discardAll }),
    [dirtyCount, registerCard, discardAll]
  )

  return (
    <WorkspaceEditGuardContext.Provider value={value}>
      {children}
    </WorkspaceEditGuardContext.Provider>
  )
}

const EMPTY_GUARD: WorkspaceEditGuard = {
  hasUnsavedChanges: false,
  registerCard: () => () => undefined,
  discardAll: () => undefined
}

/** A card rendered outside a workspace still edits and saves; it just cannot guard. */
const useWorkspaceEditGuard = () =>
  useContext(WorkspaceEditGuardContext) ?? EMPTY_GUARD

function useRegisterWorkspaceCard(
  id: string,
  dirty: boolean,
  discard: () => void
) {
  const { registerCard } = useWorkspaceEditGuard()
  const discardRef = useRef(discard)
  discardRef.current = discard

  useEffect(
    () => registerCard(id, { dirty, discard: () => discardRef.current() }),
    [registerCard, id, dirty]
  )
}

export {
  type WorkspaceEditGuard,
  WorkspaceEditGuardProvider,
  useWorkspaceEditGuard,
  useRegisterWorkspaceCard
}
